import { useEffect, useState } from 'react'

import { JSONStringify } from '../temporalHelpers'

/**
 * /!\ The state can be null while the data is fetched from storage (which could become asynchronous).
 * Don't forget to handle these cases:
 * - You will see a re-render when storage value becomes available
 * - Don't render while you are waiting for the value
 *
 * @param storageKey unique key used for localStorage
 * @param defaultState value to be used if nothing is found in localStorage or if cached data is invalid
 * @param isCachedStateValid returns false if cached data is invalid, defaultState will be used then
 */

class InvalidLocalStorageValue extends Error {
  public constructor(message: string) {
    super(message)
    this.stack = new Error().stack
    this.name = 'InvalidLocalStorageValue'
  }
}

function usePersistedState<IState>(
  storageKey: string,
  defaultState: IState,
  isCachedStateValid: (cachedState: IState) => boolean = () => true
): [IState, (newState: IState) => void] {
  const [state, setState] = useState<IState>(defaultState)

  const getPersistedState = async () => {
    try {
      const cachedState: IState = JSON.parse(localStorage.getItem(storageKey) || '')
      if (cachedState !== null && isCachedStateValid(cachedState)) {
        setState(cachedState)
      } else {
        throw new InvalidLocalStorageValue(`Value stored in localStorage with key ${storageKey} is invalid`)
      }
    } catch {
      setState(defaultState)
    }
  }

  const setPersistedState = (newState: IState) => {
    setState(newState)
    localStorage.setItem(storageKey, JSONStringify(newState))
  }

  useEffect(() => {
    getPersistedState()
  }, [])

  return [state, setPersistedState]
}

export default usePersistedState
